import flatten from 'lodash/flatten';
import zip from 'lodash/zip';
import differenceBy from 'lodash/differenceBy';

import {BasicFunnelStep, DateKeyedFunnelSegments, FunnelSegment, FunnelStep} from 'common/report/models/funnels/types';
import {BarData, Step} from 'common/widgets/funnels-chart/types';
import {OVERALL_PROP_NAME} from 'common/report/models/funnels/constants';
import {FunnelsQuery} from 'common/report/models/funnels/funnels-query';
import {ColorChoice} from 'common/report/util/funnels/colors';
import {hasData} from 'common/report/util/funnels/utils';
import {getTopPathRenamedSegments} from 'common/report/models/funnels/utils';
import {createBlankSteps, getPropertyFilterLabels, getSegments} from 'common/report/data/funnels/utils';

export function getSegmentSteps(
  query: FunnelsQuery,
  selected: ColorChoice[],
  segmentData: DateKeyedFunnelSegments[],
  comparisonSegmentData: DateKeyedFunnelSegments[],
  fromComparisonData = false,
): FunnelSegment[] {
  const selectedMap = new Map<string, number>(selected.map((s, index) => [s.key, index] as [string, number]));

  return (
    getTopPathRenamedSegments(query, fromComparisonData ? comparisonSegmentData : segmentData)
      .filter((segment) => selectedMap.has(segment.prop))
      // sort by index (keep them in the same order as the table)
      .sort((a, b) => selectedMap.get(a.prop) - selectedMap.get(b.prop))
  );
}

export function getSegmentStepsWithComparison(
  query: FunnelsQuery,
  selected: ColorChoice[],
  segmentData: DateKeyedFunnelSegments[],
  comparisonSegmentData: DateKeyedFunnelSegments[],
): FunnelSegment[] {
  const segments = getSegmentSteps(query, selected, segmentData, comparisonSegmentData);
  const comparisonSegments = reconcileComparisonSegments({
    segments,
    comparisonSegments: getSegmentSteps(query, selected, segmentData, comparisonSegmentData, true),
  });
  return flatten(zip(comparisonSegments, segments));
}

export function segmentStepsToChartSteps(
  query: FunnelsQuery,
  selected: ColorChoice[],
  segmentData: DateKeyedFunnelSegments[],
  comparisonSegmentData: DateKeyedFunnelSegments[],
): Step[] {
  if (!hasData(segmentData)) {
    return [];
  }

  const steps = getSegments(segmentData)[0].steps;
  const funnelSegments = hasData(comparisonSegmentData)
    ? getSegmentStepsWithComparison(query, selected, segmentData, comparisonSegmentData)
    : getSegmentSteps(query, selected, segmentData, comparisonSegmentData);
  const labels = query.getStepLabels();
  const isAnyOrderFunnel = query.isAnyOrderFunnel();
  const isQuerySegmented = query.isSegmented();
  const isQueryGroupedSteps = query.isGroupedSteps();

  return formatFunnelSteps(steps, funnelSegments, {
    labels,
    isAnyOrderFunnel,
    isQuerySegmented,
    isQueryGroupedSteps,
  });
}

const funnelSegmentToBarData = (funnelSegment: FunnelSegment): BarData[] =>
  funnelSegment?.steps?.map((step, idx) => {
    const {count, step_conv_ratio: stepConversionRatio} = step;
    return {
      conversionRate: stepConversionRatio,
      dropOffRate: 1 - stepConversionRatio,
      title: funnelSegment.propName,
      value: count,
      conversionToNext: funnelSegment.steps[idx + 1] && funnelSegment.steps[idx + 1].step_conv_ratio,
    };
  });

function formatFunnelSteps(
  steps: Array<BasicFunnelStep | FunnelStep>,
  funnelSegments: FunnelSegment[],
  {
    labels,
    isAnyOrderFunnel,
    isQuerySegmented,
    isQueryGroupedSteps,
  }: {labels: string[]; isAnyOrderFunnel: boolean; isQuerySegmented: boolean; isQueryGroupedSteps: boolean},
): Step[] {
  // FunnelSegment[] -> [[barDataStep1, barDataStep2], [barDataStep1, barDataStep2]]
  const segmentBarData: BarData[][] = funnelSegments.map(funnelSegmentToBarData);

  return steps.map((step, idx) => {
    const title = labels[idx];
    let tooltipText = title;
    let propertyFilterText = ``;
    if (!isAnyOrderFunnel) {
      propertyFilterText = getPropertyFilterLabels(step).propertyFilterText;
      if (propertyFilterText) {
        tooltipText = `${tooltipText} when ${propertyFilterText}`;
      }
    }
    const data = segmentBarData.map((segSteps) => {
      let barData = segSteps[idx];
      if (barData.title === OVERALL_PROP_NAME && !isQuerySegmented && !isQueryGroupedSteps) {
        // Use the actual step name, not "Overall"
        barData = {...barData, title: tooltipText};
      }
      return barData;
    });
    return {
      title,
      subheader: propertyFilterText,
      data,
      tooltipText,
      ...(data.length === 0 ? {empty: true} : null),
    };
  });
}

/**
 * Removes segments from 'comparisonSegments' that don't exist in 'segments', sorts in the same order as 'segments',
 * and adds missing (blank) segments to 'comparisonSegments'.
 */
export function reconcileComparisonSegments({
  segments,
  comparisonSegments,
  updatePropName = false,
}: {
  segments: FunnelSegment[];
  comparisonSegments: FunnelSegment[];
  updatePropName?: boolean;
}): FunnelSegment[] {
  const propToIndexMap = new Map(segments.map((segment, index) => [segment.prop, index]));

  // Remove segments that don't exist in 'segments'
  comparisonSegments = comparisonSegments.filter((segment) => propToIndexMap.get(segment.prop) !== undefined);

  // Match order of 'segments'
  comparisonSegments.sort((a, b) => propToIndexMap.get(a.prop) - propToIndexMap.get(b.prop));

  // Add blank segments
  const missingSegments = differenceBy(segments, comparisonSegments, `prop`);
  const blankMissingSegmentsWithIndex: Array<[FunnelSegment, number]> = missingSegments.map((segment) => [
    {...segment, ...{steps: createBlankSteps(segment)}},
    segments.indexOf(segment),
  ]);
  blankMissingSegmentsWithIndex.forEach(([segment, index]) => comparisonSegments.splice(index, 0, segment));

  if (updatePropName) {
    comparisonSegments = comparisonSegments.map((segment) => ({...segment, propName: `${segment.propName} (past)`}));
  }

  return comparisonSegments;
}
